import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { BsStarFill, BsStar } from "react-icons/bs";
import "../styles/ProductListBlock.css";

const API_BASE =
  process.env.REACT_APP_API_BASE ||
  process.env.VITE_API_BASE ||
  "http://localhost:8080";

const RECOMMENDATIONS_LIMIT = 18;

function getProductImages(p) {
  const firstImage = Array.isArray(p?.images) ? p.images[0] : null;

  return [
    firstImage?.image_proxy_url,
    firstImage?.image_url,
    p?.image_proxy_url,
    p?.image_url,
  ].filter(Boolean);
}

function renderRating(rating = 0) {
  const r = Math.round(Number(rating || 0));

  return [1, 2, 3, 4, 5].map((i) =>
    i <= r ? (
      <BsStarFill key={i} className="ratingStarFilled" />
    ) : (
      <BsStar key={i} className="ratingStarEmpty" />
    )
  );
}

export default function AlternativeProductsBlock({
  excludeProductId,
  onOpen,
  onAdd,
}) {
  const { t, i18n } = useTranslation("product");

  const locale = i18n.language?.startsWith("uk") ? "uk-UA" : "en-US";

  function formatPrice(price) {
    const n = Number(price || 0);

    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(n);

    return `${t("currency_symbol")}${formatted}`;
  }

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let ignore = false;

    async function loadProducts() {
      try {
        setLoading(true);

        const res = await fetch(
          `${API_BASE}/api/catalog/recommendations?limit=${RECOMMENDATIONS_LIMIT}&exclude_id=${encodeURIComponent(
            excludeProductId || ""
          )}&lang=${i18n.language}`
        );

        const data = await res.json();
        const raw = Array.isArray(data?.items) ? data.items : [];

        if (!ignore) {
          setProducts(raw.slice(0, RECOMMENDATIONS_LIMIT));
        }
      } catch (err) {
        console.error("Failed to load recommended products:", err);
        if (!ignore) setProducts([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    }

    loadProducts();

    return () => {
      ignore = true;
    };
  }, [excludeProductId, i18n.language]);

  if (loading || !products.length) return null;

  function getBeerTagValue(product, key) {
  const target = String(key || "").toLowerCase();

  const sources = [
    product?.tags,
    product?.tag_names,
    product?.product_tags,
    product?.attributes,
    product?.attribute_values,
  ];

  const flat = sources
    .filter(Array.isArray)
    .flat()
    .map((x) => {
      if (typeof x === "string") return x;

      return (
        x?.name ||
        x?.display_name ||
        x?.value ||
        x?.label ||
        x?.title ||
        ""
      );
    })
    .filter(Boolean);

  const found = flat.find((x) =>
    String(x).toLowerCase().includes(target)
  );

  if (!found) return null;

  const match = String(found).match(/(\d+(?:[.,]\d+)?)/);
  return match ? match[1].replace(",", ".") : null;
}

  return (
    <div className="block recommendedBlock">
      <div className="blockTitle">{t("recommended_products")}</div>

      <div
        className="productsSlider"
        role="list"
        aria-label={t("recommended_products")}
      >
        {products.map((p) => {
          const productId = p.variant_id || p.product_id || p.id;
          const imageCandidates = getProductImages(p);

          const hasOld =
            p.old_price != null &&
            Number(p.old_price) > 0 &&
            Number(p.price) > 0 &&
            Number(p.old_price) > Number(p.price);

          const ratingAvg = Number(p.rating_avg || 0);
          const ratingCount = Number(p.rating_count || 0);
          const abv = p?.abv || getBeerTagValue(p, "abv");
const ibu = p?.ibu || getBeerTagValue(p, "ibu");

          return (
            <div key={productId} className="pCard" role="listitem">
              <div className="pImgWrap">
  {imageCandidates.length ? (
    <>
      <img
        src={imageCandidates[0]}
        alt={p.name || "Product"}
        loading="lazy"
        className="pImg"
        onClick={() => onOpen?.(p)}
        style={{ cursor: "pointer" }}
        onError={(e) => {
          const currentSrc = e.currentTarget.getAttribute("src");
          const currentIndex = imageCandidates.indexOf(currentSrc);
          const nextSrc = imageCandidates[currentIndex + 1];

          if (nextSrc) {
            e.currentTarget.src = nextSrc;
          } else {
            e.currentTarget.style.display = "none";
          }
        }}
      />

      {(abv || ibu) && (
        <div className="pProductTags recTags">
          {ibu && (
            <span className="pTag recTag" data-label="IBU">
              <span className="pTagValue">{ibu}</span>
            </span>
          )}

          {abv && (
            <span className="pTag recTag" data-label="ABV">
              <span className="pTagValue">{abv}%</span>
            </span>
          )}
        </div>
      )}
    </>
  ) : (
    <div className="pImgFallback">{t("no_image")}</div>
  )}
</div>

              <button
                type="button"
                className="pNameBtn"
                title={p.name}
                onClick={() => onOpen?.(p)}
              >
                {p.name}
              </button>

              <div className="productRating">
                <div className="productRatingStars">
                  {renderRating(ratingAvg)}
                </div>

                <span className="productRatingCount">({ratingCount})</span>
              </div>

              <div className="pPriceRow">
                <div className="pPrice">{formatPrice(p.price)}</div>

                {hasOld && (
                  <div className="pOldPrice" aria-label={t("old_price")}>
                    {formatPrice(p.old_price)}
                  </div>
                )}
              </div>

              <button
                className="pBtn"
                onClick={() =>
                  onAdd?.({
                    ...p,
                    id: p.variant_id || p.product_id || p.id,
                  })
                }
              >
                {t("add_to_cart")}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}